import {
  analyticsReportEnvelopeSchema,
  analyticsScraperConfigListSchema,
  analyticsScraperConfigSchema,
  analyticsScraperConfigUpdateSchema,
  analyticsSessionSchema,
  type AnalyticsScraperConfig,
  type AnalyticsScraperConfigUpdate,
  type AnalyticsReportEnvelope,
  type AnalyticsSection,
  type AnalyticsSession,
} from '@sia/analytics-contracts';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiRequest(path: string, init?: RequestInit) {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  const body = await response.json().catch(() => null) as { message?: string } | null;
  if (!response.ok) throw new ApiError(body?.message ?? 'Request failed.', response.status);
  return body;
}

export async function getSession(): Promise<AnalyticsSession> {
  return analyticsSessionSchema.parse(await apiRequest('/api/session'));
}

export async function requestOtp(email: string) {
  await apiRequest('/api/auth/request-otp', { method: 'POST', body: JSON.stringify({ email }) });
}

export async function verifyOtp(email: string, token: string) {
  return analyticsSessionSchema.parse(await apiRequest('/api/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, token }),
  }));
}

export async function logout() {
  await apiRequest('/api/auth/logout', { method: 'POST' });
}

export async function getAnalyticsReport(options: {
  section: AnalyticsSection;
  from: string;
  toExclusive: string;
  groupId: number | null;
  refresh: boolean;
  signal?: AbortSignal;
}): Promise<AnalyticsReportEnvelope> {
  const params = new URLSearchParams({
    from: options.from,
    to: options.toExclusive,
    platform: 'all',
  });
  if (options.groupId) params.set('groupId', String(options.groupId));
  if (options.refresh) params.set('refresh', '1');
  return analyticsReportEnvelopeSchema.parse(await apiRequest(
    `/api/analytics/${options.section}?${params}`,
    { signal: options.signal },
  ));
}

export async function getScraperConfigurations(): Promise<AnalyticsScraperConfig[]> {
  return analyticsScraperConfigListSchema.parse(await apiRequest('/api/scraper-configs'));
}

export async function updateScraperConfiguration(
  source: AnalyticsScraperConfig['source'],
  update: AnalyticsScraperConfigUpdate,
): Promise<AnalyticsScraperConfig> {
  return analyticsScraperConfigSchema.parse(await apiRequest(`/api/scraper-configs/${source}`, {
    method: 'PATCH',
    body: JSON.stringify(analyticsScraperConfigUpdateSchema.parse(update)),
  }));
}
